/**
 * Admin 「외부 연동」 API — 파트너·연동 키·웹훅 서명 키 관리.
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — 「구현 계약 › PhAI Admin API」
 *
 * 경로·요청·응답 타입은 계약 그대로다(백엔드가 같은 계약으로 동시에 구현). 응답 래퍼는
 * `{ error, data, message }`이고, 이 레이어에서 언랩해 `data`만 돌려준다.
 * 실패는 두 갈래를 모두 `PartnerAdminError`로 바꿔 던진다.
 *  - axios 오류(4xx·5xx): 본문 `message`(Nest HttpException — 문자열 또는 ValidationPipe의 배열)
 *  - HTTP 200인데 `error !== 0`: 본문 `message`
 * 화면은 `error.message`를 그대로 토스트로 보여준다(훅 onError).
 */
import type { AxiosResponse } from "axios";
import axiosInstance from "./axiosInstance";

const BASE = "/api/v1/admin/partners";

/**
 * 상대 서버로 자동 전달하는 요청(키 발급·서명 키 생성)의 타임아웃.
 * 서버의 전달 타임아웃이 8초라 전역 10초로는 응답이 오기 전에 끊길 수 있다. 그러면 서버에는
 * 키가 생겼는데 원문을 한 번도 보지 못한다. 그래서 이 두 요청만 넉넉히 둔다.
 */
const PROVISION_TIMEOUT_MS = 30000;

// ---------------------------------------------------------------------------
// 타입 — 계약 원문 그대로
// ---------------------------------------------------------------------------

export type PartnerEnv = "LOCAL" | "DEV" | "REL";

export interface PartnerMeta {
  env: PartnerEnv | string;
  codes: { code: string; label: string }[];
  consentProfiles: { value: string; label: string }[];
  encryptionReady: boolean;
}

export type WebhookSecretSource = "db" | "env" | "constant" | "none";

export interface WebhookSecretView {
  source: WebhookSecretSource;
  /** 원문 SHA-256 앞 8자 */
  fingerprint: string | null;
  dbSource: "input" | "generated" | null;
  setAt: string | null;
}

export interface PartnerSummary {
  id: number;
  name: string;
  code: string | null;
  userId: string;
  userEmail: string | null;
  isActive: boolean;
  autoReviewOnCollect: boolean;
  consentProfile: string | null;
  callbackDomains: string[];
  provisionUrl: string | null;
  activeKeyCount: number;
  /** 키들 중 최신 */
  lastUsedAt: string | null;
  webhookSecret: WebhookSecretView;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKeyView {
  id: number;
  label: string | null;
  /** key_hash 앞 8자 */
  fingerprint: string;
  ipAllowlist: string[];
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  /**
   * 폐기 안 된 키에 원문이 보관돼 있는가(`raw_key_enc IS NOT NULL AND revoked_at IS NULL`).
   * true → [원문 복사], false → [원문 등록]. 칸을 모르는 옛 서버는 undefined로 오고, 그때는 두 버튼을 모두 숨긴다.
   */
  rawAvailable?: boolean;
}

export interface PartnerEventView {
  id: number;
  action: string;
  apiKeyId: number | null;
  actorEmail: string | null;
  changes: unknown;
  createdAt: string;
}

export interface ProvisionResult {
  ok: boolean;
  status: number | null;
  message: string;
}

export interface PartnerDetail {
  partner: PartnerSummary;
  keys: ApiKeyView[];
  /** 이력 최근 100건 */
  events: PartnerEventView[];
}

export interface CreatePartnerBody {
  name: string;
  code: string;
  userEmail: string;
  callbackDomains: string[];
  autoReviewOnCollect: boolean;
  consentProfile?: string | null;
  provisionUrl?: string | null;
}

export interface UpdatePartnerBody {
  name?: string;
  callbackDomains?: string[];
  autoReviewOnCollect?: boolean;
  consentProfile?: string | null;
  provisionUrl?: string | null;
  isActive?: boolean;
}

export interface IssueKeyBody {
  label: string;
  ipAllowlist: string[];
  provision?: boolean;
}

export interface IssueKeyResult {
  key: ApiKeyView;
  rawKey: string;
  provision: ProvisionResult | null;
  /** 발급과 함께 원문이 암호화 저장됐는가(암호화 미준비면 false). 원문 창 안내 문구를 가른다 */
  rawStored?: boolean;
}

export interface UpdateKeyBody {
  label?: string;
  ipAllowlist?: string[];
}

export interface RevealKeyResult {
  rawKey: string;
}

export interface RegisterRawKeyResult {
  key: ApiKeyView;
}

export interface RevokeKeyBody {
  reason?: string;
}

export interface WebhookSecretResult {
  webhookSecret: WebhookSecretView;
}

export interface GenerateSecretResult {
  webhookSecret: WebhookSecretView;
  rawSecret: string;
  provision: ProvisionResult | null;
}

/** 응답 래퍼 — PhAI 공통 `{ error, data, message }`. K-MRS 접근코드 서비스도 재사용한다. */
export interface Envelope<T> {
  error: number;
  data?: T;
  message?: string | string[];
  /** 실패 응답(error≠0)이 함께 싣는 문자열 code */
  code?: string;
}

// ---------------------------------------------------------------------------
// 오류
// ---------------------------------------------------------------------------

export class PartnerAdminError extends Error {
  /** HTTP 상태. 응답이 없으면(네트워크·타임아웃) null */
  status: number | null;
  /** 래퍼의 error 값(HTTP 200 + error≠0일 때) */
  code: number | null;
  /**
   * 응답 본문의 문자열 code(예: `RAW_KEY_DECRYPT_FAILED`·`RAW_KEY_MISMATCH`). 화면이 이 값으로 분기한다
   * (복호화·불일치면 그 행에 [원문 등록] 보조 버튼). 없으면 null.
   */
  bodyCode: string | null;

  constructor({
    message,
    status,
    code,
    bodyCode = null,
  }: {
    message: string;
    status: number | null;
    code: number | null;
    bodyCode?: string | null;
  }) {
    super(message);
    this.name = "PartnerAdminError";
    this.status = status;
    this.code = code;
    this.bodyCode = bodyCode;
    Object.setPrototypeOf(this, PartnerAdminError.prototype);
  }
}

/** Nest 본문 message는 문자열 또는 문자열 배열(ValidationPipe)이다. */
const pickMessage = (raw: unknown): string | null => {
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (Array.isArray(raw)) {
    const parts = raw.filter((m): m is string => typeof m === "string" && m.trim().length > 0);
    if (parts.length > 0) return parts.join(" / ");
  }
  return null;
};

/** 본문의 문자열 code(예: RAW_KEY_DECRYPT_FAILED). 없으면 null. */
const pickBodyCode = (raw: unknown): string | null =>
  typeof raw === "string" && raw.trim() ? raw.trim() : null;

/** axios 오류(또는 그 밖의 예외)를 화면에 보여줄 메시지를 가진 오류로 바꾼다. */
export const toPartnerAdminError = (err: unknown, fallback: string): PartnerAdminError => {
  if (err instanceof PartnerAdminError) return err;
  const e = err as { response?: { status?: number; data?: { message?: unknown; code?: unknown } }; code?: string };
  const response = e?.response;
  if (response) {
    const status = typeof response.status === "number" ? response.status : null;
    const fromBody = pickMessage(response.data?.message);
    let message = fromBody ?? fallback;
    if (!fromBody && status === 403) message = "관리자 권한이 필요합니다.";
    return new PartnerAdminError({ message, status, code: null, bodyCode: pickBodyCode(response.data?.code) });
  }
  if (e?.code === "ECONNABORTED") {
    return new PartnerAdminError({
      message: "서버 응답이 늦어 결과를 확인하지 못했습니다. 화면을 새로 불러와 확인하세요.",
      status: null,
      code: null,
    });
  }
  return new PartnerAdminError({ message: fallback, status: null, code: null });
};

export const unwrap = <T>(body: Envelope<T> | undefined, fallback: string): T => {
  if (!body || typeof body !== "object" || body.error !== 0) {
    throw new PartnerAdminError({
      message: pickMessage(body?.message) ?? fallback,
      status: 200,
      code: typeof body?.error === "number" ? body.error : null,
      bodyCode: pickBodyCode(body?.code),
    });
  }
  return body.data as T;
};

export const call = async <T>(request: () => Promise<AxiosResponse<Envelope<T>>>, fallback: string): Promise<T> => {
  let res: AxiosResponse<Envelope<T>>;
  try {
    res = await request();
  } catch (err) {
    throw toPartnerAdminError(err, fallback);
  }
  return unwrap(res?.data, fallback);
};

// ---------------------------------------------------------------------------
// 엔드포인트 — 계약 표 순서
// ---------------------------------------------------------------------------

/** GET /admin/partners/meta */
export const getPartnerMeta = (): Promise<PartnerMeta> =>
  call(() => axiosInstance.get(`${BASE}/meta`), "외부 연동 설정 정보를 불러오지 못했습니다.");

/** GET /admin/partners */
export const listPartners = (): Promise<PartnerSummary[]> =>
  call(() => axiosInstance.get(BASE), "파트너 목록을 불러오지 못했습니다.");

/** GET /admin/partners/:id */
export const getPartnerDetail = (id: number): Promise<PartnerDetail> =>
  call(() => axiosInstance.get(`${BASE}/${id}`), "파트너 정보를 불러오지 못했습니다.");

/** POST /admin/partners */
export const createPartner = (body: CreatePartnerBody): Promise<PartnerSummary> =>
  call(() => axiosInstance.post(BASE, body), "파트너를 등록하지 못했습니다.");

/** PATCH /admin/partners/:id */
export const updatePartner = (id: number, body: UpdatePartnerBody): Promise<PartnerSummary> =>
  call(() => axiosInstance.patch(`${BASE}/${id}`, body), "파트너 정보를 저장하지 못했습니다.");

/** POST /admin/partners/:id/keys */
export const issueApiKey = (id: number, body: IssueKeyBody): Promise<IssueKeyResult> =>
  call(
    () => axiosInstance.post(`${BASE}/${id}/keys`, body, { timeout: PROVISION_TIMEOUT_MS }),
    "연동 키를 발급하지 못했습니다."
  );

/** PATCH /admin/partners/:id/keys/:keyId */
export const updateApiKey = (id: number, keyId: number, body: UpdateKeyBody): Promise<ApiKeyView> =>
  call(() => axiosInstance.patch(`${BASE}/${id}/keys/${keyId}`, body), "연동 키를 저장하지 못했습니다.");

/** POST /admin/partners/:id/keys/:keyId/revoke */
export const revokeApiKey = (id: number, keyId: number, body: RevokeKeyBody = {}): Promise<ApiKeyView> =>
  call(
    () => axiosInstance.post(`${BASE}/${id}/keys/${keyId}/revoke`, body),
    "연동 키를 폐기하지 못했습니다."
  );

/**
 * POST /admin/partners/:id/keys/:keyId/reveal — 보관된 원문을 꺼낸다(D-3).
 * GET이 아니라 POST다(캐시·프록시 로그에 원문이 남지 않게). 응답 원문은 화면에 렌더하지 않고 클립보드로만 쓴다.
 */
export const revealApiKey = (id: number, keyId: number): Promise<RevealKeyResult> =>
  call(() => axiosInstance.post(`${BASE}/${id}/keys/${keyId}/reveal`), "원문을 가져오지 못했습니다.");

/**
 * PUT /admin/partners/:id/keys/:keyId/raw — 원문이 없는 기존 키에 원문을 보관한다(D-4).
 * 서버가 `sha256(rawKey) === key_hash`일 때만 저장한다. 키 값은 바뀌지 않는다.
 */
export const registerRawApiKey = (id: number, keyId: number, rawKey: string): Promise<RegisterRawKeyResult> =>
  call(() => axiosInstance.put(`${BASE}/${id}/keys/${keyId}/raw`, { rawKey }), "원문을 등록하지 못했습니다.");

/** PUT /admin/partners/:id/webhook-secret */
export const setWebhookSecret = (id: number, webhookSecret: string): Promise<WebhookSecretResult> =>
  call(
    () => axiosInstance.put(`${BASE}/${id}/webhook-secret`, { webhookSecret }),
    "웹훅 서명 키를 저장하지 못했습니다."
  );

/** POST /admin/partners/:id/webhook-secret/generate */
export const generateWebhookSecret = (id: number, body: { provision?: boolean }): Promise<GenerateSecretResult> =>
  call(
    () =>
      axiosInstance.post(`${BASE}/${id}/webhook-secret/generate`, body, { timeout: PROVISION_TIMEOUT_MS }),
    "웹훅 서명 키를 만들지 못했습니다."
  );

/** DELETE /admin/partners/:id/webhook-secret */
export const clearWebhookSecret = (id: number): Promise<WebhookSecretResult> =>
  call(() => axiosInstance.delete(`${BASE}/${id}/webhook-secret`), "웹훅 서명 키 DB 값을 지우지 못했습니다.");
